import React from "react";
import { Navigate, Outlet, Route, useLocation } from "react-router";
import Cookie from "js-cookie";
import Profile from "@/Pages/Profile";
import CreateHub from "@/Pages/CreateHub";
import ResetPassword from "@/Pages/Auth/ResetPassword";
import EmailVerification from "@/Pages/Auth/EmailVerification";
import ForgotPasswordContainer from "@/Pages/Auth/ForgotPassword/ForgotPasswordContainer";
import ResearchData from "@/Pages/ResearchData/ResearchData";
import NoMatch from "@/Pages/NoMatch";
import Dashboard from "@/Pages/Dashboard";
import useRebelContext from "@/hooks/useRebelContext";
import useSetInviteToken from "@/hooks/useSetInviteToken";
import SignInContainer from "@/Pages/Auth/SignIn/SignInContainer";
import LoadingComponent from "@/components/Loading/loadingComponent";
import Invitation from "@/Pages/Accept/Invitation";
import ScrollToTop from "@/components/AppLayout/MainLayout/components/ScrollToTop";
import Toast from "@/components/common/Toast";
import DoRedirect from "@/components/DoRedirect";
import { invitationTypes } from "@/store/invitations/invitationActions";
import * as Lazy from "./lazyRoutes";
import {
  EmptyLayoutOutlet,
  MainLayoutOutlet,
  ProtectedOutlet,
} from "./components/RouteWrapper";
import { withParams } from "./components/withParams";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import { eventRoutes, hubRoutes, projectRoutes } from "./components/RebelRouter";

const CONTACT_US_URL = {
  ENG: "https://rebelbase.co/contact-us",
  ESP: "https://rebelbase.co/es/contact-us/",
};

/**
 * Root layout. Owns the app-wide chrome plus the entry redirects that used to
 * run at the top of the old <Switch>.
 */
const RootLayout = () => {
  const { userId, lang } = useRebelContext();
  const { pathname, search } = useLocation();
  const isLoggedIn = !!Cookie.get("token");

  // Side effect only: captures an invitation token off the sign-up query string.
  useSetInviteToken(pathname, search);

  if (pathname === "/contact-us") {
    window.location.replace(CONTACT_US_URL[lang] ?? CONTACT_US_URL.ENG);
    return <LoadingComponent />;
  }

  const atEntryPoint = pathname === "/" || pathname.includes("/auth");

  if (isLoggedIn && atEntryPoint) {
    // Signed in and sitting on a pre-auth page — send them to the app.
    // Before the profile resolves there is nowhere to send them yet, so wait.
    if (userId) return <Navigate to="/dashboard" replace />;
    return <LoadingComponent />;
  }

  if (!isLoggedIn && pathname === "/") {
    return <Navigate to="/auth/login" replace />;
  }

  return (
    <>
      <ScrollToTop />
      <Outlet />
      <Toast />
      <DoRedirect />
    </>
  );
};

export const routes = (
  <Route element={<RootLayout />} errorElement={<RouteErrorBoundary />}>
    {/* Invitation acceptance — reachable while signed out. */}
    <Route
      path="/accept/create-hub-invitation"
      element={<Invitation invitationType={invitationTypes.createHub} />}
    />
    <Route
      path="/accept/hub-event-invitation"
      element={<Invitation invitationType={invitationTypes.hubEventInvitation} />}
    />
    <Route
      path="/accept/hub-invitation"
      element={<Invitation invitationType={invitationTypes.hubInvitation} />}
    />
    <Route
      path="/accept/project-invitation"
      element={<Invitation invitationType={invitationTypes.projectInvitation} />}
    />
    <Route path="/email-verification" element={<EmailVerification />} />
    <Route path="/research-data-2020" element={<ResearchData />} />

    {/* Pre-auth pages, on the bare layout. */}
    <Route element={<EmptyLayoutOutlet />}>
      <Route path="/forgot-password" element={<ForgotPasswordContainer />} />
      <Route path="/accept/reset-password" element={<ResetPassword />} />
      <Route path="/auth/login" element={<SignInContainer />} />
      <Route path="/auth/sign-up" element={withParams(Lazy.SignUp)} />
    </Route>

    {/* Everything else runs inside the app chrome. */}
    <Route element={<MainLayoutOutlet />}>
      {eventRoutes}
      {projectRoutes}
      {hubRoutes}

      {/* Profile reads :u_id with useParams, so it needs no withParams shim. */}
      <Route path="/profile/:u_id" element={<Profile />} />
      <Route path="/browse/*" element={withParams(Lazy.Browse)} />

      <Route element={<ProtectedOutlet />}>
        <Route path="/dashboard" element={withParams(Dashboard)} />
        <Route path="/notifications" element={withParams(Lazy.Notifications)} />
        <Route path="/invitations" element={withParams(Lazy.Invitations)} />
        <Route path="/settings" element={withParams(Lazy.Settings)} />
        <Route path="/create-hub/:h_id" element={withParams(CreateHub)} />
        <Route path="/create-hub/:h_id/:step" element={withParams(CreateHub)} />
      </Route>

      <Route path="*" element={<NoMatch />} />
    </Route>
  </Route>
);
